import React, {ReactNode, useEffect, useState} from 'react';
import axios from 'axios';
import Nav from 'react-bootstrap/Nav';
import {Dropdown, Row} from 'react-bootstrap';
import {FormModal} from 'components/modals';
import {ModalAlert} from '@/components/alerts';
import {Tooltip} from '@/components/tooltips';
import {Button} from '@/components/ui';
import Icon from '@/components/icons/Icon';
import Col from 'react-bootstrap/Col';
import {Link, navigate} from '@reach/router';
import StoryImportForm from './StoryImportForm';
import StorySettingsForm from './StorySettingsForm';
import StorySharingForm from './StorySharingForm';
import StoryUserAccessTable from './StoryUserAccessTable';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import {buildURL, buildStoryURL} from '@/utils/urlBuilder';
import {useStoreActions} from 'store';
import {Story} from 'entities/story';
import TableComponent from '@/components/table/Table';
import ConditionalNav from '@/components/navigation/ConditionalNav';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';

import {StoryListType} from '@/types/StoryListType';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import useStoriesPaginatedData from '@/views/stories/useStoriesPaginatedData';

interface Props {
    storyListType: StoryListType;
}


const StoryListView = React.memo<Props>(({storyListType}) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const setSearchQueryState = async (setSearchQuery: any, value: string, setPage: any) => {
        setPage(1);
        setSearchQuery(value);
    };
    const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);
    const [searchQuery, setSearchQuery] = useState('');


    const [importModalOpen, setImportModalOpen] = useState(false);

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [sharingModalOpen, setSharingModalOpen] = useState<boolean>(false);
    const [sharingFormKey, setSharingFormKey] = useState<number>(Math.random());
    const [activeStoryId, setActiveStoryId] = useState<number | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<{id: number, title: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const addOrEditStory = useStoreActions((actions) => actions.stories.addOrEditStory);
    const deleteStory = useStoreActions((actions) => actions.stories.deleteStory);

    const {
        stories,
        loading,
        currentPage,
        setCurrentPage,
        pages,
    } = useStoriesPaginatedData(storyListType, searchQuery);

    const canCreateStory = hasPermission('fs.story.create', userData?.permissions, true) || false;
    const canChangeStory = hasPermission('fs.story.update', userData?.permissions, true) || false;
    const canDeleteStory = hasPermission('fs.story.delete', userData?.permissions, true) || false;
    const canViewStoryUserAccess = hasPermission('fs.story.list', userData?.permissions, true) || false;
    const canCloneStory = hasPermission('fs.story.clone', userData?.permissions, true) || false;

    const userCanAccessStoryResource = hasPermission('fs.story.list', userData?.permissions, true) || false;

    const userCanAccessStoryDetailView = hasPermission('fs.story.list', userData?.permissions, true) || false;
    const userCanAccessStoryTemplateDetailView = hasPermission('fs.story.list', userData?.permissions, true) || false;

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    let helpTextBlock;
    if (storyListType === StoryListType.SharedStories) {
        helpTextBlock = (
            <HelpTextBlock helpText={i18n.t('ht_this_view_stories_shared_with_you.text')} />
        );
    } else {
        helpTextBlock = (
            <HelpTextBlock helpText={i18n.t('ht_use_stories.text')} />
        );
    }

    if (!userCanAccessStoryResource && !loadingUserData) {
        return (
            <>
                <Nav variant="pills" role="tablist" className="bg-white mb-3">
                    {storyListType === StoryListType.MyStories ? (
                        <Nav.Item>
                            <Link
                                to="../my-stories"
                                className={`nav-link ${storyListType === StoryListType.MyStories ? 'active' : ''}`}
                            >
                                {i18n.t('jt_stories.text')}
                            </Link>
                        </Nav.Item>
                    ) : (
                        <Nav.Item>
                            <Link
                                to="../shared-stories"
                                className={`nav-link ${storyListType === StoryListType.SharedStories ? 'active' : ''}`}
                            >
                                {i18n.t('jt_stories_shared.text')}
                            </Link>
                        </Nav.Item>
                    )}
                    {storyListType === StoryListType.SharedStories ? null : (
                        <Tooltip
                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                            placement="top"
                        >
                            <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                <Button
                                    variant="outline-secondary"
                                    className='mb-2'
                                    size="sm"
                                    icon="mdi-plus"
                                    disabled
                                    style={{pointerEvents: 'none'}}
                                    data-cy="story-list-plus-outlinesecondary-btn">
                                    {i18n.t('th_add_new_storyboard.text')}
                                </Button>
                            </div>
                        </Tooltip>
                    )}
                </Nav>

                {helpTextBlock}

                <div className="alert alert-danger" role="alert"> {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const openImportModal = () => {
        setImportModalOpen(true);
        setActiveStoryId(null);
        setSettingsModalOpen(false);
    };

    const closeImportModal = () => {
        setImportModalOpen(false);
    };

    const openSettingsModal = (id: number): void => {
        setActiveStoryId(id);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = () => {
        setActiveStoryId(null);
        setSettingsModalOpen(false);
    };

    const openSharingModal = (id: number): void => {
        setActiveStoryId(id);
        setSharingModalOpen(true);
    };

    const closeSharingModal = () => {
        setActiveStoryId(null);
        setSharingModalOpen(false);
    };

    const resetSharingForm = () => {
        setSharingFormKey(Math.random());
    };

    const openDeleteConfirm = (id: number, title: string): void => {
        setItemToDelete({id, title});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setItemToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!itemToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = buildStoryURL('storyDetails', itemToDelete.id);
            await axios.delete(url);
            deleteStory(itemToDelete.id);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteOne = (id: number, title: string): void => {
        openDeleteConfirm(id, title);
    };

    const duplicate = async (id: number): Promise<void> => {
        const response = await axios.post(buildStoryURL('cloneStory', id), {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        addOrEditStory(response.data);
    };

    const accessTooltip = (cell: string, row: Story): ReactNode => {
        if (!row.visibleTo || row.visibleTo.length === 1) {
            return null;
        }

        const visibleUsers = row.visibleTo.slice(0, 5);
        const notShownUsers = row.visibleTo.length - visibleUsers.length;

        return (
            <Tooltip
                content={
                    <div>
                        {visibleUsers.map((u) => (
                            <span className="d-block" key={u.id}>
                                {u.username}
                            </span>
                        ))}
                        {notShownUsers ? (
                            <span className="d-block">
                                {`+ ${notShownUsers} more`}
                            </span>
                        ) : null}
                    </div>
                }
                placement="auto"
            >
                {row.userIsAdmin && canViewStoryUserAccess ? (
                    <Button
                        variant="link"
                        size="sm"
                        icon="mdi-account-multiple"
                        iconSize="sm"
                        className="ml-2 text-info"
                        onClick={() => openSharingModal(row.id)}
                        style={{padding: '0', border: 'none', background: 'none'}}
                    />
                ) : (
                    <Button
                        variant="link"
                        size="sm"
                        icon="mdi-account-multiple"
                        iconSize="sm"
                        className="ml-2 text-info"
                        disabled
                        style={{padding: '0', border: 'none', background: 'none'}}
                        data-cy="story-list-link-btn"
                    />
                )}
            </Tooltip>
        );
    };

    const detailUrlGenerator = (rowData: any) => {
        if (!rowData.storyHashid && userCanAccessStoryTemplateDetailView) {
            return buildURL('storyTemplateDetailView', {story_assigned_url: rowData.assignedUrl});
        } else if (userCanAccessStoryDetailView) {
            return buildURL('storyStoryboardView', {
                storyboard_id: rowData.firstStoryboardHashid,
                story_id: rowData.firstStoryboardSlug
            });
        }
        return null;
    };

    const columns = [
        {
            accessorKey: 'title',
            header: i18n.t('th_name.text'),
            cell: (cell: any) => (
                <span className="d-inline-flex align-items-center flex-nowrap text-nowrap">
                    <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />
                    {accessTooltip(cell.row.original.title, cell.row.original)}
                </span>
            ),
        },
        {
            accessorKey: 'createdBy.userDisplayName',
            header: i18n.t('th_owner.text'),
        },
        {
            id: 'lastUpdated',
            accessorKey: 'lastUpdated',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: any) => {
                if (!cell.lastUpdated || cell.lastUpdated === '-') {
                    return configLoaded ? formatDateTime(cell.createdOn, true, {setZone: false},
                    ) : '';
                }

                return configLoaded ? formatDateTime(cell.lastUpdated, true, {setZone: false},
                ) : '';
            },
        },
        {
            accessorKey: 'noOfStoryboards',
            header: i18n.t('th_number_storyboards.text'),
            cell: (cell: any) => Object.keys(cell.row.original.storyboards).length,
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => {
                const url = detailUrlGenerator(cell.row.original);

                return (
                    <div className="d-flex align-items-center justify-content-end w-100">
                        <div className="d-inline-block align-middle mr-2">
                            {
                                url ? (
                                    <Tooltip content={i18n.t('jt_view.text')} placement="top">
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            icon="mdi-eye"
                                            data-cy="story-view-btn"
                                            onClick={() => navigate(url)}
                                        >
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                        placement="top"
                                    >
                                        <div className="d-inline-block align-middle" style={{cursor: 'not-allowed'}}>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                icon="mdi-eye"
                                                disabled
                                                style={{pointerEvents: 'none'}}
                                                data-cy="story-list-eye-outlinesecondary-btn">
                                            </Button>
                                        </div>
                                    </Tooltip>
                                )
                            }
                        </div>
                        <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                            <Dropdown className="d-inline-block align-middle">
                                <Dropdown.Toggle
                                    as={Button}
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-dots-horizontal"
                                    className="dropdown-toggle-no-caret"
                                    data-cy="story-settings-toggle"
                                    id="settings-dropdown-1"
                                />

                                <Dropdown.Menu align="end">
                                    {
                                        (cell.row.original.userIsAdmin && canChangeStory) ? (
                                            <Dropdown.Item
                                                onClick={() => openSettingsModal(cell.row.original.id)}
                                                data-cy="story-settings-btn"
                                            >
                                                <Icon name="mdi-settings" size="sm" className="mr-2" /> {i18n.t('jt_settings.text')}
                                            </Dropdown.Item>
                                        ) : (
                                            <Tooltip
                                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                placement="top"
                                            >
                                                <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                    <Dropdown.Item
                                                        style={{pointerEvents: 'none'}}
                                                        disabled
                                                        data-cy="story-settings-open-button"
                                                    >
                                                        <Icon name="mdi-settings" size="sm" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                    </Dropdown.Item>
                                                </div>
                                            </Tooltip>
                                        )
                                    }

                                    {
                                        (cell.row.original.userIsAdmin && canCloneStory) ? (
                                            <Dropdown.Item
                                                onClick={() => duplicate(cell.row.original.id)}
                                                data-cy="story-settings-duplicate-btn"
                                            >
                                                <Icon name="mdi-content-copy" className="mr-2" /> {i18n.t('jt_duplicate.text')}
                                            </Dropdown.Item>
                                        ) : (
                                            <Tooltip
                                                placement="top"
                                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            >
                                                <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                    <Dropdown.Item
                                                        style={{pointerEvents: 'none'}}
                                                        disabled
                                                        data-cy="story-settings-duplicate-btn"
                                                    >
                                                        <Icon name="mdi-content-copy" className="mr-2" /> {i18n.t('jt_duplicate.text')}
                                                    </Dropdown.Item>
                                                </div>
                                            </Tooltip>
                                        )
                                    }

                                    <Dropdown.Item
                                        href={buildURL('storiesImportDetailResource', {obj_id: cell.row.original.id})} data-cy="story-list-btn-3">
                                        <Icon name="mdi-export" className="mr-2" /> {i18n.t('jt_export.text')}
                                    </Dropdown.Item>

                                    {
                                        (cell.row.original.userIsAdmin && canViewStoryUserAccess) ? (
                                            <Dropdown.Item
                                                onClick={() => openSharingModal(cell.row.original.id)}
                                                data-cy="story-settings-share-btn"
                                            >
                                                <Icon name="mdi-share-outline" className="mr-2" /> {i18n.t('jt_sharing.text')}
                                            </Dropdown.Item>
                                        ) : (
                                            <Tooltip
                                                placement="top"
                                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            >
                                                <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                    <Dropdown.Item
                                                        style={{pointerEvents: 'none'}}
                                                        disabled
                                                        data-cy="story-settings-share-btn"
                                                    >
                                                        <Icon name="mdi-share-outline" className="mr-2" /> {i18n.t('jt_sharing.text')}
                                                    </Dropdown.Item>
                                                </div>
                                            </Tooltip>
                                        )
                                    }

                                    {
                                        (cell.row.original.userIsAdmin && canDeleteStory) ? (
                                            <Dropdown.Item
                                                onClick={() => deleteOne(cell.row.original.id, cell.row.original.title)}
                                                data-cy="story-settings-delete-btn"
                                            >
                                                <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                            </Dropdown.Item>
                                        ) : (
                                            <Tooltip
                                                placement="top"
                                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            >
                                                <div className="ml-auto" style={{cursor: 'not-allowed', display: 'flex'}}>
                                                    <Dropdown.Item
                                                        style={{pointerEvents: 'none'}}
                                                        disabled
                                                        data-cy="story-list-btn">
                                                        <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                                    </Dropdown.Item>
                                                </div>
                                            </Tooltip>
                                        )
                                    }
                                </Dropdown.Menu>
                            </Dropdown>
                        </Tooltip>
                    </div>
                );
            },
        },
    ]

    return (
        <>
            <ConditionalNav>
            </ConditionalNav>

            {helpTextBlock}
            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end align-items-center">
                    {storyListType === StoryListType.SharedStories ? null : (
                        canCreateStory ? (
                            <Button
                                variant="info"
                                className='mb-2'
                                size="sm"
                                icon="mdi-plus"
                                onClick={() => setSettingsModalOpen(true)}
                                data-cy="new-story-btn"
                            >
                                {i18n.t('th_add_new_storyboard.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                placement="top"
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                            >
                                <div style={{cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                    <Button
                                        variant="outline-secondary"
                                        size="xs"
                                        icon="mdi-plus"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="new-story-btn"
                                    >
                                        {i18n.t('th_add_new_storyboard.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    )}
                </Col>
            </Row>

            <LoadingWrapper
                loading={loading}
                loadingText={i18n.t('jt_loading_data_general.text')}
                minHeight="400px"
            >
                <div className="card">
                    <div className="card-body stories-list-table">
                        <TableComponent data={stories} columns={columns} />

                        <PaginationComponent
                            pages={pages}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            hideWhenSinglePage={false}
                        />
                    </div>
                </div>
            </LoadingWrapper>

            {
                importModalOpen ? (
                    <FormModal
                        isOpen={importModalOpen}
                        onClose={closeImportModal}
                        title={i18n.t('jt_import_story.text')}
                        size="xl"
                    >
                        <StoryImportForm
                            onDone={closeImportModal}
                        />
                    </FormModal>
                ) : null
            }

            {
                settingsModalOpen ? (
                    <FormModal
                        isOpen={settingsModalOpen}
                        onClose={closeSettingsModal}
                        title={
                            <div className="d-flex align-items-center">
                                {i18n.t('story_settings.text')}
                                {
                                    !activeStoryId ? (
                                        <Button
                                            variant="link"
                                            icon="mdi-open-in-new"
                                            iconPosition="right"
                                            onClick={openImportModal}
                                            style={{padding: '0', marginLeft: '8px'}}
                                            data-cy="story-list-storysettings-text-btn">
                                            {i18n.t('import_story.text')}
                                        </Button>
                                    ) : null
                                }
                            </div>
                        }
                    >
                        <StorySettingsForm
                            storyId={activeStoryId}
                            onDone={closeSettingsModal}
                        />
                    </FormModal>
                ) : null
            }

            {
                sharingModalOpen ? (
                    <FormModal
                        isOpen={sharingModalOpen}
                        onClose={closeSharingModal}
                        title={
                            <div>
                                <p>{i18n.t('jt_share_the_story.text')}</p>
                                <p className="h6">
                                    {activeStoryId ? stories.find((s) => s.id === activeStoryId)!.title : ''}
                                </p>
                            </div>
                        }
                    >
                        <h5>{i18n.t('jt_give_access.text')}</h5>
                        <StorySharingForm
                            key={sharingFormKey}
                            storyId={activeStoryId}
                            onFinish={resetSharingForm}
                        />

                        <h5 className="mt-3">{i18n.t('jt_edit_access.text')}</h5>
                        <StoryUserAccessTable
                            storyId={activeStoryId}
                        />
                    </FormModal>
                ) : null
            }
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={itemToDelete ? i18n.t('confirm_delete_story.text') + ` ${itemToDelete.title}` : ''}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});

export default StoryListView;
